import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { MAX_PHONE_LENGTH, validatePhone } from '../../utils/phone';

export interface SectionEditField {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'phone' | 'select' | 'date';
  options?: string[];
  keyboardType?: 'default' | 'email-address' | 'phone-pad';
}

interface Props {
  visible: boolean;
  title: string;
  fields: SectionEditField[];
  initialValues: Record<string, string>;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}

export function SectionEditModal({ visible, title, fields, initialValues, onClose, onSubmit }: Props) {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleChange = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Phone numbers are held to the server's 20-character column here first, so the
    // parent is told before the request leaves rather than after a generic failure.
    for (const field of fields) {
      if (field.type !== 'phone') continue;
      const error = validatePhone(values[field.key], field.label);
      if (error) {
        Alert.alert('Check the ' + field.label.toLowerCase(), error);
        return;
      }
    }
    try {
      setSaving(true);
      await onSubmit(values);
    } catch (err: any) {
      console.error('SectionEditModal save failed:', err);
      // The server names the problem (a duplicate email, a phone that is too long);
      // that message beats an apology the parent cannot act on.
      const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
      Alert.alert('Error', serverMessage || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} disabled={saving}>
              <FeatherIcon name="x" size={20} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {fields.map(field => (
              <View key={field.key} style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>{field.label}</Text>
                {field.type === 'select' ? (
                  <View style={styles.chipRow}>
                    {(field.options || []).map(opt => {
                      const selected = values[field.key] === opt;
                      return (
                        <TouchableOpacity
                          key={opt}
                          style={[styles.chip, selected && styles.chipSelected]}
                          onPress={() => handleChange(field.key, opt)}
                        >
                          <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{opt}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ) : field.type === 'date' && Platform.OS === 'web' ? (
                  // A real HTML5 date input opens the browser's own calendar picker
                  // and guarantees a parseable YYYY-MM-DD value, unlike a free-text
                  // box. No React Native equivalent exists, so native keeps TextInput.
                  React.createElement('input', {
                    type: 'date',
                    value: values[field.key] || '',
                    onChange: (e: any) => handleChange(field.key, e.target.value),
                    style: {
                      boxSizing: 'border-box',
                      width: '100%',
                      height: 44,
                      paddingLeft: 14,
                      paddingRight: 14,
                      borderRadius: 12,
                      border: '1px solid rgb(226, 232, 240)',
                      fontSize: 15,
                      outline: 'none',
                      backgroundColor: '#F8FAFC',
                      color: '#1E293B',
                      fontFamily: 'inherit',
                    },
                  })
                ) : (
                  <TextInput
                    style={styles.input}
                    value={values[field.key] || ''}
                    onChangeText={text => handleChange(field.key, text)}
                    keyboardType={field.keyboardType || (field.type === 'email' ? 'email-address' : field.type === 'phone' ? 'phone-pad' : 'default')}
                    maxLength={field.type === 'phone' ? MAX_PHONE_LENGTH : undefined}
                  />
                )}
              </View>
            ))}
          </ScrollView>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={saving}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#FFF" size="small" /> : <Text style={styles.saveText}>Save</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  title: {
    fontSize: 17,
    fontWeight: '800',
    color: '#1E293B',
  },
  closeBtn: {
    padding: 4,
  },
  body: {
    maxHeight: 420,
  },
  bodyContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  } as any,
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  chipSelected: {
    backgroundColor: '#1E40AF',
    borderColor: '#1E40AF',
  },
  chipText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  chipTextSelected: {
    color: '#FFFFFF',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
  },
  cancelText: {
    color: '#475569',
    fontWeight: '700',
    fontSize: 14,
  },
  saveBtn: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 10,
    backgroundColor: '#1E40AF',
    minWidth: 90,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
});
